import BellIcon from "@heroicons/react/24/solid/BellIcon";
import Bars3Icon from "@heroicons/react/24/solid/Bars3Icon";
import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  IconButton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ScrollingText from "./ScrollingText";
import { usePopover } from "../../hooks/use-popover";
import { AccountPopover } from "../popover/account-popover";
import { NotificationPopover } from "../popover/notification-popover";
import { NotaryNotification } from "../notary/NotaryNotification";
import {
  setPrevTourStep,
  setShowTourNav,
} from "../../store/actions/dataActions";
import { useTour } from "@reactour/tour";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import axios from "axios";
const TOP_NAV_HEIGHT = 64;

const TopNav = ({ onNavOpen, togleNav, openNav, setTourOpen }) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);
  const { pathname } = useLocation();
  const [isAdminRemote, setIsAdminRemote] = useState(
    typeof window !== "undefined" &&
      window.sessionStorage.getItem("adminRemote") === "true",
  );
  const notifications = useSelector((state) => state.notifications);
  const noc_data = useSelector((state) => state.nocData?.nocData);
  const realTimeRequests = useSelector(
    (state) => state.notarySession?.notaryRequests,
  );

  // Memoize the realTimeRequests to avoid creating new array references
  const memoizedRealTimeRequests = useMemo(() => {
    return realTimeRequests || [];
  }, [realTimeRequests]);
  useEffect(() => {
    try {
      setIsAdminRemote(
        typeof window !== "undefined" &&
          window.sessionStorage.getItem("adminRemote") === "true",
      );
    } catch (e) {
      setIsAdminRemote(false);
    }
  }, [currentSession, pathname]);
  const { setCurrentStep } = useTour();
  const dispatch = useDispatch();

  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const isBetween1200And1450 = useMediaQuery(
    "(min-width:1200px) and (max-width:1450px)",
  );
  const accountPopover = usePopover();
  const notificationPopover = usePopover();
  const [failedCounties, setFailedCounties] = useState([]);
  const [expSubText, setExpSubText] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");

  const renewDateObj = useMemo(() => {
    return new Date(currentUser?.user?.cancellation_time * 1000);
  }, [currentUser?.user?.cancellation_time]);

  const handleNotaryLogin = async () => {
    try {
      // Get the notary user data from the current subuser
      const notaryUser = currentUser?.subuser
        ? currentUser?.subuser?.notary_user
        : currentUser?.user?.notary_user;
      if (!notaryUser?.bn_registered || !notaryUser?.on_site_registered) {
        console.error("No notary user data found");
        return;
      }

      // Call the notary login from subuser endpoint
      const { data: result } = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/auth/notary-login-from-subuser`,
        {
          notaryUser,
          currentSession,
        },
        { withCredentials: true },
      );

      if (result?.success) {
        // Open notary dashboard in new tab with the notary session
        // The backend has already set the notary session cookie
        window.open("/notary/dashboard", "_blank");
      } else {
        console.error("Notary login failed:", result?.message);
      }
    } catch (error) {
      console.error("Error during notary login:", error);
    }
  };

  useEffect(() => {
    if (currentUser?.avatar) {
      setAvatarUrl(currentUser?.avatar);
    }
  }, [currentUser?.avatar]);

  useEffect(() => {
    if (currentUser?.user?.expired) {
      if (!currentUser?.subuser) {
        setExpSubText(
          'Payment Failed *** Your subscription has expired! Please create a new subscription to continue using Permit Rockstar by visiting the "Manage Subscription" page.',
        );
      } else {
        setExpSubText(
          "Payment Failed *** Your subscription has expired! Please contact your Administrator to create a  new subscription to continue using Permit Rockstar.",
        );
      }
    } else if (currentUser?.user?.cancellation_time) {
      if (!currentUser?.subuser) {
        setExpSubText(
          `Canceled Subscription *** Your subscription will be canceled on ${renewDateObj.toLocaleDateString(
            "en-US",
          )}. You can reactivate it anytime on the "Manage Subscription" page.`,
        );
      } else {
        setExpSubText(
          `Canceled Subscription *** Your subscription will be canceled on ${renewDateObj.toLocaleDateString(
            "en-US",
          )}. Please contact your Administrator to reactivate it.`,
        );
      }
    } else if (noc_data) {
      if (noc_data?.failed_counties?.length === 0) {
        setExpSubText("");
        setFailedCounties("");
      } else {
        setFailedCounties(noc_data?.failed_counties);
      }
    } else {
      setExpSubText("");
    }
  }, [currentUser, noc_data, expSubText, renewDateObj]);

  const notificationTimezone =
    currentUser?.role === "notary"
      ? currentUser?.timezone || "America/New_York"
      : currentUser?.subuser?.timezone ||
        currentUser?.user?.personal?.[0]?.timezone ||
        "America/New_York";

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          spacing={2}
          sx={{
            minHeight: TOP_NAV_HEIGHT,
            px: 2,
          }}
        >
          {["/custom-documents/sign"].includes(pathname) && lgUp && openNav && (
            <Box
              sx={{
                position: "absolute",
                height: TOP_NAV_HEIGHT,
                zIndex: 99999999,
                left: isBetween1200And1450 ? -180 : -150,
                top: 9,
              }}
            >
              <img
                style={{
                  width: "6rem",
                  marginTop: "-9px",
                }}
                alt="Permit Rockstar Logo"
                src="/img/website_img/logoText.png"
              />
            </Box>
          )}
          <Stack alignItems="center" direction="row" spacing={2}>
            {["/custom-documents/sign"].includes(pathname) ? (
              <IconButton onClick={togleNav}>
                <SvgIcon fontSize="small">
                  <Bars3Icon />
                </SvgIcon>
              </IconButton>
            ) : (
              !lgUp && (
                <IconButton onClick={onNavOpen}>
                  <SvgIcon fontSize="small">
                    <Bars3Icon />
                  </SvgIcon>
                </IconButton>
              )
            )}
          </Stack>
          <div className="scroll-wrapper">
            <ScrollingText
              expiredSubscription={expSubText}
              failedCounties={failedCounties}
            />
          </div>

          <Stack alignItems="center" direction="row" spacing={2}>
            {isAdminRemote && (
              <Tooltip title="Exit Remote Connection">
                <IconButton
                  size="small"
                  onClick={async () => {
                    try {
                      let adminEmail = "";
                      try {
                        adminEmail =
                          window.sessionStorage.getItem("adminRemoteEmail") ||
                          "";
                      } catch (e) {
                        adminEmail = "";
                      }

                      await axios.post(
                        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/remote-connection-exit`,
                        {
                          currentSession,
                          admin_email: adminEmail,
                        },
                        { withCredentials: true },
                      );
                      try {
                        window.sessionStorage.removeItem("adminRemote");
                        window.sessionStorage.removeItem("adminRemoteEmail");
                        // NOTE: do NOT clear adminRemoteCustomersState here.
                        // It will be cleared after the Customers page reads it and restores state.
                      } catch (e) {
                        // ignore
                      }
                      window.location.href = "/admin/customers";
                    } catch (e) {
                      console.error("Failed to exit remote connection", e);
                    }
                  }}
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 2,
                    backgroundColor: "error.main",
                    color: "error.contrastText",
                    "&:hover": {
                      backgroundColor: "error.dark",
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={{ textTransform: "uppercase" }}
                  >
                    Exit
                  </Typography>
                </IconButton>
              </Tooltip>
            )}
            {currentUser?.role !== "notary" &&
            currentUser?.user?.role !== "privateProvider" ? (
              <>
                {lgUp && (
                  <Tooltip title="Start Guided Tour">
                    <span>
                      <IconButton
                        disabled={currentUser?.user?.expired ? true : false}
                        onClick={() => {
                          setCurrentStep(0);
                          dispatch(setShowTourNav(false));
                          dispatch(setPrevTourStep(0));
                          setTourOpen(true);
                        }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography
                            sx={{ mb: -1 }}
                            variant="body2"
                            fontWeight="bold"
                            color={
                              currentUser?.user?.expired
                                ? "textSecondary"
                                : !lgUp
                                  ? "textSecondary"
                                  : "primary"
                            }
                          >
                            Guided
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color={
                              currentUser?.user?.expired
                                ? "textSecondary"
                                : !lgUp
                                  ? "textSecondary"
                                  : "primary"
                            }
                          >
                            Tour
                          </Typography>
                        </Box>
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </>
            ) : (
              ""
            )}

            {/* Notary Button - Show when user (main or subuser) has an active notary setup and is not already logged in as notary */}
            {(() => {
              // Subuser: still rely on subuser.notary_user.is_notary flag
              const shouldShowNotaryButton =
                (currentUser?.subuser &&
                  currentUser?.subuser?.notary_user?.on_site_registered &&
                  currentUser?.subuser?.notary_user?.bn_registered &&
                  currentUser?.subuser?.role !== "notary" &&
                  currentUser?.subuser?.role !== "privateProvider") ||
                // Main user: check converted notary status flags (on_site_registered + bn_registered)
                (!currentUser?.subuser &&
                  (() => {
                    const convertedNotaryUser =
                      currentUser?.user?.notary_user || null;

                    return (
                      convertedNotaryUser &&
                      convertedNotaryUser.on_site_registered &&
                      convertedNotaryUser.bn_registered &&
                      currentUser?.user?.role !== "notary"
                    );
                  })());

              return shouldShowNotaryButton;
            })() && (
              <>
                {lgUp ? (
                  <Tooltip title="Open Notary Dashboard">
                    <span>
                      <IconButton
                        disabled={currentUser?.user?.expired ? true : false}
                        onClick={handleNotaryLogin}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography
                            sx={{ mb: -1 }}
                            variant="body2"
                            fontWeight="bold"
                            color={
                              currentUser?.user?.expired
                                ? "textSecondary"
                                : !lgUp
                                  ? "textSecondary"
                                  : "primary"
                            }
                          >
                            📜
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color={
                              currentUser?.user?.expired
                                ? "textSecondary"
                                : !lgUp
                                  ? "textSecondary"
                                  : "primary"
                            }
                          >
                            Notary
                          </Typography>
                        </Box>
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <IconButton disabled>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        sx={{ mb: -1 }}
                        variant="body2"
                        fontWeight="bold"
                        color={!lgUp ? "textSecondary" : "primary"}
                      >
                        📜
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={!lgUp ? "textSecondary" : "primary"}
                      >
                        Notary
                      </Typography>
                    </Box>
                  </IconButton>
                )}
              </>
            )}

            <Tooltip
              title="Notifications"
              sx={
                currentUser?.role === "notary"
                  ? { visibility: "hidden" }
                  : { visibility: "block" }
              }
            >
              <span>
                <IconButton
                  disabled={currentUser?.user?.expired ? true : false}
                  onClick={() => {
                    notificationPopover.handleOpen();
                  }}
                  ref={notificationPopover.anchorRef}
                >
                  <Badge
                    badgeContent={
                      currentSession === "notarySession"
                        ? (notifications?.totalNewCount || 0) +
                          memoizedRealTimeRequests.length
                        : notifications?.totalNewCount || null
                    }
                    color={
                      currentSession === "notarySession" &&
                      memoizedRealTimeRequests.length > 0
                        ? "error"
                        : "success"
                    }
                    variant="number"
                  >
                    <SvgIcon fontSize="medium">
                      <BellIcon />
                    </SvgIcon>
                  </Badge>
                </IconButton>
              </span>
            </Tooltip>
            <Avatar
              onClick={accountPopover.handleOpen}
              ref={accountPopover.anchorRef}
              sx={{
                cursor: "pointer",
                height: 40,
                width: 40,
              }}
              src={avatarUrl}
            />
          </Stack>
        </Stack>
      </Box>
      <AccountPopover
        setAvatarUrl={setAvatarUrl}
        avatarUrl={avatarUrl}
        anchorEl={notificationPopover.anchorRef.current}
        open={accountPopover.open}
        onClose={accountPopover.handleClose}
      />
      {currentUser?.role === "notary" ? (
        <NotaryNotification
          anchorEl={notificationPopover.anchorRef.current}
          open={notificationPopover.open}
          onClose={notificationPopover.handleClose}
        />
      ) : (
        <NotificationPopover
          anchorEl={notificationPopover.anchorRef.current}
          open={notificationPopover.open}
          onClose={notificationPopover.handleClose}
          timezone={notificationTimezone}
        />
      )}
    </>
  );
};

export default TopNav;
